import type { BotMessageDeliveryStatus, CCVoiceToken, CallScriptAgentView } from "@swivl-mono/types";
import type {
  AgentOutboundCallResult,
  AgentOutboundTextResult,
  BotMessageSeenBy,
  CoachingNudgeInterface,
  CoachingScoreSnapshotInterface,
  ConversationFocusSource,
  DebugLevel
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CCEscalationResult {
  success: boolean;
  assignmentId?: number;
  agentMemberId?: number;
  error?: string;
}

export interface CCConversationChannelInfo {
  channel: string;
  botConversationId: number;
  lastMessageContent?: string;
  lastMessageAt?: string;
  isEscalated: boolean;
  agentMemberName?: string;
  userOnline: boolean;
  /** Whether the user is actively viewing this channel (panel open) — seeds the teal "viewing" dot on load, not just on live status_changed. */
  userViewing: boolean;
  /** Visible user messages above the member's persisted read watermark (ENG-2827) — drives the list badge. */
  unreadCount: number;
  /** The conversation counter's current position — in-place list patching baseline (ENG-2827). */
  latestMessageIndex: number;
}

export interface CCConversationListItem {
  userId: number;
  userName: string;
  userPhone?: string;
  channels: CCConversationChannelInfo[];
  locationId?: number;
  locationName?: string;
  isEscalated: boolean;
  assignedToMe: boolean;
  /** The requesting member's accepted-assignment id when this is their active takeover — lets the list end it in place. */
  activeAssignmentId?: number;
  /** That assignment's channel — a voice takeover's row action is Hang up, not End Takeover (ENG-3448). */
  activeAssignmentChannel?: string;
  lastActiveAt: string;
}

export interface CCConversationListResponse {
  myEscalations: CCConversationListItem[];
  otherConversations: CCConversationListItem[];
}

export interface CCMessageItem {
  id: number;
  /** Canonical uuid — the cross-surface dedup/reconcile key (ENG-2825). */
  messageId: string;
  messageIndex: number;
  role: string;
  content: string;
  senderName?: string;
  channel: string;
  timestamp: string;
  visible: boolean;
  type: string;
  metadata?: Record<string, unknown>;
  /** FK to the BotLLMCall record — lets llm_call cards lazy-load the full payload (admin tier). */
  botLLMCallId?: number;
  invisibleTier?: 1 | 2;
  /** Outbound transmission state — drives the agent timeline's delivery ticks (web). */
  deliveryStatus?: string;
  /** Why a message was simulated or blocked by the Outbound Gate (ENG-3373), or a provider error code. */
  deliveryReason?: string;
  /** Per-reader read receipts (customer + members) — drives the "seen" tick. */
  seenBy?: BotMessageSeenBy;
  /** Structured-message protocol fields — without these the fetched copy of a structured card loses its payload. */
  contentType?: string;
  payload?: { kind: string; data: unknown };
  actionTaken?: unknown;
}

// ─── Pending Offers (ENG-2826) ───────────────────────────────────────────────

export interface CCPendingOffer {
  assignmentId: number;
  botConversationId: number;
  channel: string;
  userId: number;
  userName: string;
  escalationReason?: string;
  offeredAt: string;
  /** Server-authoritative deadline (ISO) — the toast countdown renders from this. */
  expiresAt: string;
}

export interface CCMissedOffer {
  assignmentId: number;
  botConversationId: number;
  channel: string;
  userId: number;
  userName: string;
  timedOutAt: string;
}

export interface CCPendingOffersResponse {
  offers: CCPendingOffer[];
  missed: CCMissedOffer[];
}

export interface CCConversationDetailResponse {
  userId: number;
  userName: string;
  channels: {
    channel: string;
    botConversationId: number;
    status: string;
    /** When this conversation's call or session ended; absent while it is live (ENG-3656: voice actions key on this, not on the user's presence). */
    endedAt?: string;
    escalationStatus?: string;
    agentMemberName?: string;
    messages: CCMessageItem[];
    summary?: string;
    /** Requesting member's read watermark before this open (-1 = never read) — anchors the "New Messages" divider. */
    memberLastReadIndex?: number;
    /** Collapsed history stack above the current conversation (oldest first). */
    previousConversations?: CCConversationHistoryItem[];
  }[];
  activeAssignment?: {
    id: number;
    status: string;
    channel: string;
    agentMemberId: number;
    agentMemberName: string;
    acceptedAt?: string;
  };
}

/** Agent-side gate-code lookup (ENG-3186). Past due is refused, same as the widget and bot. */
/**
 * `isPastDue` rides along with a SUCCESSFUL lookup (ENG-3191): unlike the widget
 * and bot, the agent surface returns a past-due tenant's code and warns, rather
 * than refusing — see the server-side type for why.
 */
export type CCAccessCodeResponse = { ok: true; accessCode: string; isPastDue: boolean } | { ok: false; reason: "unavailable" };

export interface CCUserDetailResponse {
  userId: number;
  name: string;
  phone?: string;
  email?: string;
  /** USER-level Open/Closed (ENG-3036): true = archived (Closed tab). */
  isArchived?: boolean;
  locationName?: string;
  locationAddress?: string;
  leases: {
    id: number;
    /** Facility this unit is at — units group under their location in the panel. */
    locationId?: number;
    locationName?: string;
    /** One-line "street, city, state" from Cortex vars; absent when unset. */
    locationAddress?: string;
    unitNumber: string;
    unitDimensions: string;
    rent: number;
    balance: number;
    /** The sync's stored status, kept for display; the past-due decision is `isPastDue`. */
    paymentStatus: string;
    /** The server's shared past-due answer as of the request (ENG-3665) — the Past Due pill keys on this. */
    isPastDue: boolean;
    /** Calendar days past due in the facility's timezone, from the same answer; 0 when not past due. */
    daysPastDue: number;
    moveInDate?: string;
    accessCode?: string;
    /** Unit shape, so the panel can pick the matching stock illustration. */
    unitWidth?: number;
    unitLength?: number;
    unitFeatures?: string[];
  }[];
  recentPayments: {
    amount: number;
    date: string;
    method?: string;
    status: string;
  }[];
  notes: {
    id: number;
    content: string;
    createdAt: string;
    createdByName?: string;
  }[];
  /** First conversation with this customer (ISO), if any (ENG-3097). */
  firstInteractionAt?: string;
  /** Most recent conversation with this customer (ISO), if any (ENG-3097). */
  lastInteractionAt?: string;
  /** Open tickets for this customer (§13.5) — click opens the ticket flyout in place. */
  tickets: {
    id: number;
    number: number;
    title: string;
    status: string;
  }[];
  gateCode?: string;
  accountNumber?: string;
}

// ─── Agent Dashboard (ENG-3052) ─────────────────────────────────────────────

/**
 * Per-channel split behind the hover-flip KPI cards. `null` means the window
 * held no measurable interaction on that channel — not zero, and the card must
 * not render it as "0s".
 */
export interface CCChannelBreakdown {
  web: number | null;
  sms: number | null;
  voice: number | null;
}

/** The dashboard's five KPI cards for one org over one window. */
export interface CCDashboardMetrics {
  from: string;
  to: string;
  /** Mean conversation health 0-100 over ENDED conversations that carry a score. */
  interactionHealth: {
    score: number | null;
    /** How many conversations went into the mean — a 92 off two calls is not a 92. */
    sampleSize: number;
  };
  /** Inbound voice: `channel = voice` AND `origin = customer`. */
  totalCalls: number;
  /** Mean score per channel, for the Interaction Health flip face. */
  healthByChannel: CCChannelBreakdown;
  /** How many conversations each channel's mean rests on (Pulse, ENG-3418). */
  healthSampleSizeByChannel: CCChannelBreakdown;
  /** Inbound (origin=customer) conversation counts per channel — Total Calls flip. */
  inboundByChannel: CCChannelBreakdown;
  /** Abandonment rate 0-1 per channel, for that card's flip face. */
  abandonmentByChannel: CCChannelBreakdown;
  /** How many judged conversations each channel's rate rests on (Pulse, ENG-3418). */
  abandonmentSampleSizeByChannel: CCChannelBreakdown;
  /** Escalation-raised → agent-accepted, in seconds. */
  avgWaitSeconds: number | null;
  avgWaitByChannel: CCChannelBreakdown;
  maxWaitSeconds: number | null;
  maxWaitByChannel: CCChannelBreakdown;
  waitSampleSize: number;
  /** Conversations that went into each channel's wait figures (Pulse, ENG-3418). */
  waitSampleSizeByChannel: CCChannelBreakdown;
  /** 0-1, not a percentage. Null when nothing in the window was judged. */
  abandonmentRate: number | null;
  abandonmentSampleSize: number;
}

/**
 * What the dashboard's `Topic(s)` column displays (ENG-3052) — four values, not
 * the routing enum's six, collapsed with precedence Emergency > Sales > Service.
 */
export type CCInteractionTopic = "Sales" | "Service" | "Emergency" | "Uncategorized";

/** Who (or what) currently has the conversation. */
export interface CCInteractionHandler {
  kind: "agent" | "ai" | "no_agents_accepted" | "no_agents_available";
  agentMemberId?: number;
  agentName?: string;
  /** The agent's avatar, when they have one (ENG-3192). Absent for AI and the no-agent kinds. */
  agentAvatarUrl?: string;
}

/** One row of the dashboard's Active Interaction table (ENG-3052). */
export interface CCActiveInteraction {
  botConversationId: number;
  userId: number;
  userName: string;
  channel: string;
  /** The one-line context under the customer's name. */
  context?: string;
  /** `waiting` = somebody is expected and hasn't arrived; otherwise engaged. */
  state: "engaged" | "waiting";
  topic: CCInteractionTopic;
  handledBy: CCInteractionHandler;
  /** Conversation start — the Duration column counts up from here. */
  startedAt: string;
  score: number | null;
  scoreVelocity: number | null;
}

/**
 * One finished conversation, as every Snowy surface that lists conversation
 * history renders it (ENG-3097). Supersedes `pastConversations`,
 * `CCInteractionHistoryItem`, the Recordings page's local row type, and
 * `CCPreviousConversation`.
 *
 * Everything past identity and timing is optional: which fields are populated
 * depends on the read, not the renderer.
 */
export interface CCConversationHistoryItem {
  botConversationId: number;
  /** Twilio call sid / chat id — the Recordings page searches on it. */
  callSid?: string;
  userId: number;
  userName: string;
  /** The customer's number, where we have one — searchable on the Recordings page. */
  userPhone?: string;
  channel: string;
  startedAt: string;
  endedAt?: string;
  /** One-line context for the collapsed row. */
  shortSummary?: string;
  /** Full AI summary, for the expansion. */
  summary?: string;
  handledBy?: CCInteractionHandler;
  /** 0-100. Colour follows the band everywhere; the arrow follows velocity. */
  score?: number;
  scoreVelocity?: number;
  /** Voice only. */
  recording?: { url: string; durationSeconds?: number };
  tickets?: CCHistoryTicket[];
  /** Visible-message count — the previous-conversation stack shows this. */
  messageCount?: number;
  endReason?: string;
  /** The sequence that started it, when one did. */
  originatingSequenceId?: number;
  /** The agent who placed it, when an agent did. */
  originMemberId?: number;
}

/** Filters for the unified history read. All independent, all optional. */
export interface CCConversationHistoryQuery {
  userId?: number;
  /** On its own: everything this agent handled, across every customer. */
  agentMemberId?: number;
  channel?: string;
  hasRecording?: boolean;
  from?: Date;
  to?: Date;
  limit?: number;
  offset?: number;
}

/** A ticket that came out of a conversation, as the history row's expansion lists it. */
export interface CCHistoryTicket {
  id: number;
  number: number;
  title: string;
  status: string;
}

/** One row of the dashboard's Active Agents table (ENG-3052). */
export interface CCDashboardAgent {
  memberId: number;
  name: string;
  avatarUrl?: string;
  /** Live presence: online | busy | away | offline. */
  state: string;
  activeAssignmentCount: number;
  /** Channels the agent is ACTIVELY HANDLING — from their accepted assignments. */
  channels: string[];
  /** Escalation & ticketing group display names this agent belongs to. */
  groups: string[];
  /** ISO start of the agent's current voice call — the "On Call • m:ss" chip counts up from here. */
  onCallSince?: string;
}

/** Pre-fill for the Assign an Agent modal (ENG-3052): the engine's ring + every org member. */
export interface CCSuggestedRing {
  /** Member ids in the exact order the engine would ring them. */
  ring: number[];
  /** All org members with live presence merged in — the roster shape. */
  agents: CCDashboardAgent[];
}

export interface CCAgentDetail {
  memberId: number;
  name: string;
  email?: string;
  avatarUrl?: string;
  activeTakeovers: Array<{
    assignmentId: number;
    botConversationId: number;
    userId: number;
    userName: string;
    channel: string;
    acceptedAt?: string;
    /** Live health score, 0-100, per takeover (ENG-3054 AC7). */
    score?: number;
    scoreVelocity?: number;
  }>;
  today: {
    offered: number;
    accepted: number;
    completed: number;
    avgHandleSeconds: number | null;
  };
}

export interface CCAvailableAgent {
  id: number;
  name: string;
  status: "available" | "busy";
  activeCount: number;
}

// ─── API ────────────────────────────────────────────────────────────────────

/** What the server returns for an agent message: the saved row, and for a text its real outcome (ENG-3281). */
export interface AgentMessageSendResult {
  id: number;
  messageIndex: number;
  /** sent / simulated / blocked / failed for a text; absent on web. */
  deliveryStatus?: BotMessageDeliveryStatus;
  /** The gate's reason or a send error code when not sent. */
  reason?: string;
}

export class ContactCenterV3API {
  // ─── Escalation (called by bot apps) ──────────────────────────────────

  static async escalate(botConversationId: number, reason?: string, department?: string): Promise<CCEscalationResult> {
    return APIService.Server.post<CCEscalationResult>("/contact-center-v3/escalate", {
      botConversationId,
      reason,
      department
    });
  }

  // ─── Conversations (called by frontend) ───────────────────────────────

  static async getConversations(
    orgId: number,
    memberId: number,
    filter?: "all" | "my_escalations" | "escalated" | "closed",
    search?: string
  ): Promise<CCConversationListResponse> {
    const params: Record<string, unknown> = { memberId };
    if (filter) params.filter = filter;
    if (search?.trim()) params.search = search.trim();
    return APIService.Server.get<CCConversationListResponse>(`/contact-center-v3/conversations/org/${orgId}`, params);
  }

  /** Close all of a user's active conversations (ENG-2791, plan 8.1) — archive-like; the bot auto-reopens if the user writes again. */
  static async closeUserConversations(orgId: number, userId: number): Promise<{ closed: number }> {
    return APIService.Server.post<{ closed: number }>(`/contact-center-v3/conversations/org/${orgId}/user/${userId}/close`, {});
  }

  /** Reopen a user's agent-closed conversations (ENG-2792). */
  static async reopenUserConversations(orgId: number, userId: number): Promise<{ reopened: number }> {
    return APIService.Server.post<{ reopened: number }>(`/contact-center-v3/conversations/org/${orgId}/user/${userId}/reopen`, {});
  }

  static async getConversationDetail(
    orgId: number,
    userId: number,
    channel?: string,
    debugLevel?: DebugLevel,
    /** Ticket deep-link (ENG-2929 C4): guarantee this conversation rides in its channel's previous stack. */
    includeConversationId?: number
  ): Promise<CCConversationDetailResponse> {
    const params: Record<string, unknown> = {};
    if (channel) params.channel = channel;
    if (debugLevel) params.debugLevel = debugLevel;
    if (includeConversationId) params.includeConversationId = includeConversationId;
    return APIService.Server.get<CCConversationDetailResponse>(`/contact-center-v3/conversations/org/${orgId}/user/${userId}`, params);
  }

  /**
   * Live gate code for one lease — fetched fresh from the FMS server-side, never
   * the cached `lease.accessCode`. POST keeps the code out of URLs and logs.
   */
  static async getAccessCodeForLease(orgId: number, leaseId: number): Promise<CCAccessCodeResponse> {
    return APIService.Server.post<CCAccessCodeResponse>(`/contact-center-v3/customers/org/${orgId}/lease/${leaseId}/access-code`);
  }

  static async getUserDetail(orgId: number, userId: number): Promise<CCUserDetailResponse> {
    return APIService.Server.get<CCUserDetailResponse>(`/contact-center-v3/customers/org/${orgId}/user/${userId}`);
  }

  // ─── Available Agents ─────────────────────────────────────────────────

  static async getAvailableAgents(orgId: number, excludeMemberId?: number): Promise<CCAvailableAgent[]> {
    const params: Record<string, unknown> = {};
    if (excludeMemberId) params.excludeMemberId = excludeMemberId;
    return APIService.Server.get<CCAvailableAgent[]>(`/contact-center-v3/agents/org/${orgId}`, params);
  }

  // ─── Agent Messages ───────────────────────────────────────────────────

  static async sendAgentMessage(
    botConversationId: number,
    memberId: number,
    content: string,
    channel: string,
    /** Client-generated uuid — the canonical messageId + idempotency key (ENG-2825). */
    messageId: string,
    /** Public S3 URL of an uploaded image attachment (ENG-2828) — saved as a structured `image` message. */
    imageUrl?: string
  ): Promise<AgentMessageSendResult> {
    return APIService.Server.post<AgentMessageSendResult>(`/contact-center-v3/conversations/${botConversationId}/messages`, {
      memberId,
      content,
      channel,
      messageId,
      imageUrl
    });
  }

  /**
   * Agent read receipt — the agent has this conversation open + on screen. The studio fires
   * this on conversation open / tab-refocus (gated on visibility), never from background
   * polls. The member is resolved from request auth on the server.
   */
  static async markConversationRead(botConversationId: number): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/conversations/${botConversationId}/read`, {});
  }

  // ─── Pending Offers (ENG-2826) ───────────────────────────────────────────

  /**
   * Live + recently-missed escalation offers for a member, with the
   * server-authoritative `expiresAt`. Called from resyncAll so offers emitted
   * while the agent was disconnected are re-delivered or surfaced as missed.
   */
  /** Voice caller hung up mid-ring — retract the pending offers (ENG-2929). */
  /** ENG-3408 — the accepted agent's leg never joined the conference: close the assignment, tell the agent. */
  static async agentLegFailed(botConversationId: number, status: string): Promise<{ closed: number }> {
    return APIService.Server.post<{ closed: number }>(`/contact-center-v3/voice/agent-leg-failed/${botConversationId}`, { status });
  }

  static async cancelPendingOffers(botConversationId: number, reason: string): Promise<{ cancelled: number }> {
    return APIService.Server.post<{ cancelled: number }>(`/contact-center-v3/conversations/${botConversationId}/cancel-pending-offers`, { reason });
  }

  static async getPendingOffers(orgId: number, memberId: number): Promise<CCPendingOffersResponse> {
    return APIService.Server.get<CCPendingOffersResponse>(`/contact-center-v3/assignments/pending/org/${orgId}/member/${memberId}`);
  }

  /** Full history of one conversation — previous-conversation card expand. */
  static async getConversationMessages(orgId: number, botConversationId: number, debugLevel?: DebugLevel): Promise<CCMessageItem[]> {
    const params = debugLevel ? { debugLevel } : undefined;
    return APIService.Server.get<CCMessageItem[]>(`/contact-center-v3/conversations/org/${orgId}/conversation/${botConversationId}/messages`, params);
  }

  static async getNewMessages(botConversationId: number, afterMessageIndex: number, debugLevel?: DebugLevel): Promise<CCMessageItem[]> {
    const params: Record<string, unknown> = {};
    if (debugLevel) params.debugLevel = debugLevel;
    return APIService.Server.get<CCMessageItem[]>(
      `/contact-center-v3/conversations/${botConversationId}/messages/since/${afterMessageIndex}`,
      params
    );
  }

  // ─── Assignments ──────────────────────────────────────────────────────

  static async acceptAssignment(assignmentId: number, memberId: number): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/assignments/${assignmentId}/accept`, { memberId });
  }

  static async rejectAssignment(assignmentId: number, memberId: number): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/assignments/${assignmentId}/reject`, { memberId });
  }

  static async completeAssignment(assignmentId: number, memberId: number, returnToBot?: boolean): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/assignments/${assignmentId}/complete`, {
      memberId,
      returnToBot
    });
  }

  static async transferAssignment(
    assignmentId: number,
    fromMemberId: number,
    toMemberId: number,
    handoffNote?: string
  ): Promise<{ newAssignmentId: number }> {
    return APIService.Server.post<{ newAssignmentId: number }>(`/contact-center-v3/assignments/${assignmentId}/transfer`, {
      fromMemberId,
      toMemberId,
      handoffNote
    });
  }

  /** Returns the conversation the takeover actually owns — a finalized thread gets a FRESH conversation (ENG-3036). */
  static async proactiveTakeover(
    botConversationId: number,
    memberId: number,
    channel: string
  ): Promise<{ assignmentId: number; botConversationId: number }> {
    return APIService.Server.post<{ assignmentId: number; botConversationId: number }>(
      `/contact-center-v3/conversations/${botConversationId}/takeover`,
      { memberId, channel }
    );
  }

  /**
   * The Agent Dashboard's five KPI cards (ENG-3052). Omit the range for the
   * default trailing-7-day window.
   */
  static async getDashboardMetrics(orgId: number, from?: Date, to?: Date): Promise<CCDashboardMetrics> {
    const params = new URLSearchParams();
    if (from) params.set("from", from.toISOString());
    if (to) params.set("to", to.toISOString());
    const query = params.toString();
    return APIService.Server.get<CCDashboardMetrics>(`/contact-center-v3/dashboard/org/${orgId}/metrics${query ? `?${query}` : ""}`);
  }

  /** Every live conversation in the org — the Active Interaction table (ENG-3052). */
  static async getActiveInteractions(orgId: number): Promise<CCActiveInteraction[]> {
    return APIService.Server.get<CCActiveInteraction[]>(`/contact-center-v3/dashboard/org/${orgId}/active`);
  }

  /** The ring the engine would build right now for this conversation, plus every org member (ENG-3052). */
  static async getSuggestedRing(orgId: number, botConversationId: number): Promise<CCSuggestedRing> {
    return APIService.Server.get<CCSuggestedRing>(`/contact-center-v3/dashboard/org/${orgId}/conversations/${botConversationId}/suggested-ring`);
  }

  /**
   * The one conversation-history read (ENG-3097). Every filter is optional:
   * `{ userId }` is a customer's history, `{ agentMemberId }` is an agent's
   * across every customer, `{ hasRecording: true }` is the recordings list.
   */
  static async getConversationHistory(orgId: number, query: CCConversationHistoryQuery = {}): Promise<CCConversationHistoryItem[]> {
    const params = new URLSearchParams();
    if (query.userId != null) params.set("userId", String(query.userId));
    if (query.agentMemberId != null) params.set("agentMemberId", String(query.agentMemberId));
    if (query.channel) params.set("channel", query.channel);
    if (query.hasRecording) params.set("hasRecording", "true");
    if (query.from) params.set("from", query.from.toISOString());
    if (query.to) params.set("to", query.to.toISOString());
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    const qs = params.toString();
    return APIService.Server.get<CCConversationHistoryItem[]>(`/contact-center-v3/conversations/history/org/${orgId}${qs ? `?${qs}` : ""}`);
  }

  /** The dashboard's Active Agents roster (ENG-3052). */
  static async getDashboardRoster(orgId: number): Promise<CCDashboardAgent[]> {
    return APIService.Server.get<CCDashboardAgent[]>(`/contact-center-v3/dashboard/org/${orgId}/roster`);
  }

  /**
   * Assign a live conversation to an ordered ring of agents (ENG-3052). The
   * offer goes to the first reachable agent in `ring`; ignoring or timing out
   * advances down it automatically.
   */
  static async assignAgentsManually(
    orgId: number,
    botConversationId: number,
    ring: number[]
  ): Promise<{ ok: boolean; assignmentId?: number; agentMemberId?: number; error?: string }> {
    return APIService.Server.post<{ ok: boolean; assignmentId?: number; agentMemberId?: number; error?: string }>(
      `/contact-center-v3/dashboard/org/${orgId}/conversations/${botConversationId}/assign`,
      { ring }
    );
  }

  /**
   * A manager takes another agent offline (ENG-3052). Offline only — nothing
   * brings an agent back online but the agent.
   */
  static async setAgentOffline(orgId: number, memberId: number): Promise<{ ok: boolean; error?: string }> {
    return APIService.Server.post<{ ok: boolean; error?: string }>(`/contact-center-v3/dashboard/org/${orgId}/member/${memberId}/offline`, {});
  }

  /** Agent detail for the dashboard slide-out panel (ENG-2946). */
  static async getAgentDetail(orgId: number, memberId: number): Promise<CCAgentDetail> {
    return APIService.Server.get<CCAgentDetail>(`/contact-center-v3/agents/org/${orgId}/member/${memberId}/detail`);
  }

  /** Pull an actively-owned conversation from its current agent (ENG-2946). */
  static async pullTakeover(botConversationId: number, memberId: number, channel: string): Promise<{ assignmentId: number }> {
    return APIService.Server.post<{ assignmentId: number }>(`/contact-center-v3/conversations/${botConversationId}/pull-takeover`, {
      memberId,
      channel
    });
  }

  static async timeoutAssignment(assignmentId: number): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/assignments/${assignmentId}/timeout`);
  }

  // ─── Voice ──────────────────────────────────────────────────────────────

  static async generateVoiceToken(orgId: number, memberId: number, sessionId?: string): Promise<{ token: string; identity: string }> {
    return APIService.Server.post<{ token: string; identity: string }>("/contact-center-v3/voice/token", { orgId, memberId, sessionId });
  }

  /** One softphone token per Twilio account the org's numbers live on (ENG-3656). */
  static async generateVoiceTokens(orgId: number, memberId: number, sessionId?: string): Promise<CCVoiceToken[]> {
    return APIService.Server.post<CCVoiceToken[]>("/contact-center-v3/voice/tokens", { orgId, memberId, sessionId });
  }

  static async listenIn(botConversationId: number, orgId: number, twilioToken: string): Promise<{ roomName: string }> {
    return APIService.Server.post<{ roomName: string }>(`/contact-center-v3/voice/listen/${botConversationId}`, { orgId, twilioToken });
  }

  static async bargeIn(botConversationId: number, orgId: number, twilioToken: string): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/voice/barge/${botConversationId}`, { orgId, twilioToken });
  }

  static async bridgeToAgent(botConversationId: number, orgId: number, twilioToken: string): Promise<{ success: boolean }> {
    return APIService.Server.post<{ success: boolean }>(`/contact-center-v3/voice/bridge/${botConversationId}`, { orgId, twilioToken });
  }

  /** Hang up a voice takeover (ENG-3448) — only the member holding the accepted voice assignment may. */
  static async hangupVoiceCall(botConversationId: number, memberId: number): Promise<{ ended: boolean }> {
    return APIService.Server.post<{ ended: boolean }>(`/contact-center-v3/voice/hangup/${botConversationId}`, { memberId });
  }

  static async initiateOutboundCall(
    orgId: number,
    memberId: number,
    toNumber: string,
    fromNumber?: string,
    twilioToken?: string,
    userId?: number,
    consentOverride?: boolean
  ): Promise<AgentOutboundCallResult> {
    return APIService.Server.post<AgentOutboundCallResult>("/contact-center-v3/voice/call", {
      orgId,
      memberId,
      toNumber,
      fromNumber,
      twilioToken,
      userId,
      consentOverride
    });
  }

  /**
   * New Conversation's Text (ENG-3592): start a 1:1 text. The agent is the signed-in member (never
   * sent in the body); a picked customer is sent as `userId` with the number shown as `toNumber`.
   */
  static async startOutboundText(
    orgId: number,
    body: { userId?: number; toNumber?: string; fromNumber?: string; content: string }
  ): Promise<AgentOutboundTextResult> {
    return APIService.Server.post<AgentOutboundTextResult>(`/contact-center-v3/text/org/${orgId}/start`, body);
  }

  static async getCallerIds(orgId: number): Promise<{ id: number; identifier: string; name: string }[]> {
    return APIService.Server.get<{ id: number; identifier: string; name: string }[]>(`/contact-center-v3/voice/caller-ids/org/${orgId}`);
  }

  static async getOutboundNumbers(orgId: number): Promise<{
    org: { identifier: string; name: string } | null;
    locations: { locationId: number; locationName: string; address: string; identifier: string }[];
    /** Active numbers not tied to the org default or a location (ENG-3490). */
    unassigned: { identifier: string; name: string }[];
  }> {
    return APIService.Server.get<{
      org: { identifier: string; name: string } | null;
      locations: { locationId: number; locationName: string; address: string; identifier: string }[];
      unassigned: { identifier: string; name: string }[];
    }>(`/contact-center-v3/voice/outbound-numbers/org/${orgId}`);
  }

  static async getPreferredFromNumber(
    orgId: number,
    memberId: number,
    userId?: number
  ): Promise<{ identifier: string | null; name: string | null; source: "location" | "agent-default" | "org-primary" }> {
    const query = userId ? `?userId=${userId}` : "";
    return APIService.Server.get<{ identifier: string | null; name: string | null; source: "location" | "agent-default" | "org-primary" }>(
      `/contact-center-v3/voice/preferred-from-number/org/${orgId}/member/${memberId}${query}`
    );
  }

  static async getVoiceParticipants(botConversationId: number): Promise<{ label: string; callSid: string; muted: boolean }[]> {
    return APIService.Server.get<{ label: string; callSid: string; muted: boolean }[]>(`/contact-center-v3/voice/participants/${botConversationId}`);
  }

  // ─── Recordings ──────────────────────────────────────────────────────────

  // ─── Typing ──────────────────────────────────────────────────────────────

  static async pushTyping(botConversationId: number, isTyping: boolean): Promise<{ success: boolean }> {
    return APIService.Server.post("/contact-center-v3/typing", { botConversationId, isTyping });
  }

  // ─── Coaching (ENG-2957) ─────────────────────────────────────────────────

  /** Right-panel reload state: latest score snapshot + full nudge list. */
  static async getCoachingState(
    orgId: number,
    botConversationId: number
  ): Promise<{
    latestSnapshot: CoachingScoreSnapshotInterface | null;
    /** Score trend: signed points per message of the last real movement (ENG-3068). */
    scoreVelocity: number | null;
    nudges: CoachingNudgeInterface[];
    focus: {
      locationId: number | null;
      locationName: string | null;
      unitId: number | null;
      unitName: string | null;
      source: ConversationFocusSource | null;
    };
    /** Accumulated conversation topics (append-only). */
    topics: string[];
    /** The script in play as agents see it (ENG-3402); null until the engine picks one. */
    script: CallScriptAgentView | null;
  }> {
    return APIService.Server.get(`/contact-center-v3/conversations/${botConversationId}/coaching?orgId=${orgId}`);
  }

  /** Pin / unpin / dismiss a nudge card. */
  static async coachingNudgeLifecycle(nudgeId: number, memberId: number, action: "pin" | "unpin" | "dismiss"): Promise<CoachingNudgeInterface> {
    return APIService.Server.post(`/contact-center-v3/coaching/nudges/${nudgeId}/lifecycle`, { memberId, action });
  }

  /** Execute a nudge action as the agent (send/text/units); the card finishes on success. */
  static async coachingNudgeExecute(
    nudgeId: number,
    body: {
      memberId: number;
      actionKind: string;
      text?: string;
      unitIds?: number[];
      /** Selected locations for the multi-send (send_locations — ENG-3191). */
      locationIds?: number[];
      locationId?: number;
      unitId?: number;
      /** Picker cards (ENG-3051): also send the picked entity to the customer / don't change focus. */
      sendCard?: boolean;
      skipSelect?: boolean;
      messageId?: string;
    }
  ): Promise<CoachingNudgeInterface> {
    return APIService.Server.post(`/contact-center-v3/coaching/nudges/${nudgeId}/execute`, body);
  }

  /** Facility JIT refresh (§9.5): queue FMS re-syncs for the user's active leases. */
  static async refreshFacilityData(orgId: number, botConversationId: number): Promise<{ refreshedAt: string; queued: number; total: number }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/facility-refresh?orgId=${orgId}`, {});
  }

  /** Selected box (2026-08-18): set or clear the conversation's focused location/unit. `null` clears. */
  static async coachingSetFocus(
    orgId: number,
    botConversationId: number,
    body: { locationId?: number | null; unitId?: number | null }
  ): Promise<{ ok: boolean }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/focus?orgId=${orgId}`, body);
  }

  /** Topic pill removal (2026-08-18): agents remove a tag; the score merge never re-adds it. */
  static async coachingRemoveTopic(orgId: number, botConversationId: number, topic: string): Promise<{ topics: string[] }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/remove-topic?orgId=${orgId}`, { topic });
  }

  /** Selected box (2026-08-18): send the selected location/unit as a structured card. */
  static async coachingSendFocusCard(
    orgId: number,
    botConversationId: number,
    body: { memberId: number; kind: "location" | "unit"; messageId?: string }
  ): Promise<{ ok: boolean }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/send-focus-card?orgId=${orgId}`, body);
  }

  /** Create-Ticket LLM prefill (§9.4): draft title/description/tags for the agent to review. */
  /** Agent toolbar (§13.7): create an agent-selected nudge card on demand. */
  static async coachingAgentNudge(
    orgId: number,
    botConversationId: number,
    body: { type: string; param?: string; memberId: number }
  ): Promise<{ nudgeId: number | null }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/agent-nudge?orgId=${orgId}`, body);
  }

  /** Policy nudge on-expand draft (§13.6). `nudgeId` is the card the draft is persisted to. */
  static async coachingPolicyDraft(
    orgId: number,
    botConversationId: number,
    nudgeId: number,
    policyKey: string,
    agentSelected?: boolean
  ): Promise<{ message: string }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/policy-draft?orgId=${orgId}`, {
      nudgeId,
      policyKey,
      agentSelected
    });
  }

  static async coachingTicketPrefill(orgId: number, botConversationId: number): Promise<{ title: string; description: string; tags: string[] }> {
    return APIService.Server.post(`/contact-center-v3/conversations/${botConversationId}/coaching/ticket-prefill?orgId=${orgId}`, {});
  }

  // ─── Agent Presence (reads only — presence is driven over the bot socket) ──

  static async getOrgPresence(
    orgId: number
  ): Promise<{ memberId: number; state: string; lastHeartbeat: string; lastActivity: string; activeAssignmentCount: number }[]> {
    return APIService.Server.get(`/contact-center-v3/presence/org/${orgId}`);
  }

  static async getPresenceTimeline(memberId: number, startDate: string, endDate: string): Promise<any[]> {
    return APIService.Server.get(`/contact-center-v3/presence/timeline/${memberId}`, { startDate, endDate });
  }
}
